// apps/web/src/lib/estoque/estorno-movimento.ts — Fase 13, Plano 35 (EST-14, EST-15; adendo "Estornar pesagem/perda" do UI-SPEC).
// Lógica pura do "Estornar movimento" na linha expandida do lote em /estoque/consumo-tinta: quem pode, rótulo do menu,
// texto de confirmação, motivo obrigatório e sucesso. A autorização real é do servidor (estoque:aprovar-ajuste); aqui
// só se decide o que renderizar. Sem motivo não há envio (EST-15).
import type { ModoPesagem } from '@pieltec/contracts';
import { formatarData, formatarDataHora, formatarDataLocal } from '../vendas/formatacao';
import { PERMISSAO_APROVAR_AJUSTE } from './inventario';
import { formatarQuantidadeEstoque } from './formatacao';

export const MOTIVO_ESTORNO_MOVIMENTO_MAXIMO = 500;
export const MENSAGEM_MOTIVO_OBRIGATORIO = 'Informe o motivo.';
export const MENSAGEM_FALHA_ESTORNO_MOVIMENTO = 'Não foi possível confirmar o estorno do movimento. Confira o histórico do lote antes de tentar de novo.';

/** O pedaço do movimento de que a lógica precisa (um `MovimentoTintaLote` satisfaz). */
export interface MovimentoEstornavel {
  readonly estornado: boolean;
}

/** Só quem tem `estoque:aprovar-ajuste` vê a ação, e movimento já estornado não tem ação alguma. */
export function podeEstornarMovimento(permissoes: readonly string[], movimento: MovimentoEstornavel): boolean {
  return permissoes.includes(PERMISSAO_APROVAR_AJUSTE) && !movimento.estornado;
}

/** "Mais ações do movimento de 29/09 14:05" (dia e hora de São Paulo, sem o ano). */
export function rotuloMenuMovimento(criadoEm: string): string {
  const dataHora = formatarDataHora(criadoEm).replace(/^(\d{2}\/\d{2})\/\d{4}/, '$1');
  return `Mais ações do movimento de ${dataHora}`;
}

const TIPO_NO_TEXTO: Readonly<Record<ModoPesagem, string>> = { SAIDA: 'saída', DEVOLUCAO: 'devolução', PERDA: 'perda' };

export interface EntradaTextoEstornoMovimento {
  readonly modo: ModoPesagem;
  readonly pesoKg: number;
  readonly tintaCodigo: string;
  readonly loteInterno: string;
  readonly criadoEm: string;
  readonly usuarioNome: string | null;
}

/** Texto do diálogo destrutivo (UI-SPEC, adendo "Estornar pesagem/perda"). Peso com até 4 casas. */
export function textoConfirmacaoEstornoMovimento(entrada: EntradaTextoEstornoMovimento): string {
  const tipo = TIPO_NO_TEXTO[entrada.modo] ?? String(entrada.modo).toLowerCase();
  const peso = formatarQuantidadeEstoque(entrada.pesoKg, 'kg');
  const usuario = entrada.usuarioNome ?? 'usuário não informado';
  return (
    `O movimento de ${tipo} de ${peso} de ${entrada.tintaCodigo} no lote ${entrada.loteInterno} ` +
    `(${formatarDataHora(entrada.criadoEm)}, ${usuario}) será estornado. O consumo real do lote será recalculado. Confirmar?`
  );
}

/** Toast/aviso de sucesso depois do estorno. */
export function textoSucessoEstornoMovimento(loteInterno: string): string {
  return `Movimento estornado. Consumo do lote ${loteInterno} recalculado.`;
}

/** Motivo obrigatório (EST-15): devolve a mensagem de erro, ou null quando está preenchido. */
export function validarMotivo(motivo: string): string | null {
  return motivo.trim().length === 0 ? MENSAGEM_MOTIVO_OBRIGATORIO : null;
}

export const MENSAGEM_SEM_PERMISSAO_ESTORNO_MOVIMENTO = 'Você não tem permissão para esta ação.';
const CODIGO_MOVIMENTO_EM_RATEIO_FECHADO = 'MOVIMENTO_EM_RATEIO_FECHADO';
const CODIGO_MOTIVO_OBRIGATORIO = 'MOTIVO_OBRIGATORIO';

/** Resposta de erro do POST estornar; `status` null = falha de rede (a requisição nem chegou ao servidor). */
export interface ErroEstornoMovimento {
  readonly status: number | null;
  readonly corpo?: unknown;
  /** Instante do movimento: dia de negócio de reserva quando o 409 não traz o campo `dia`. */
  readonly movimentoCriadoEm?: string;
}

/**
 * Mensagem do erro do estorno de movimento (UI-SPEC, linha "error / Estornar pesagem/perda"):
 * 409 MOVIMENTO_EM_RATEIO_FECHADO cita o dia do rateio (campo `dia`, ou o dia do movimento); 400 MOTIVO_OBRIGATORIO e 403 têm
 * texto próprio; qualquer outra falha (inclusive rede) cai na mensagem genérica, sem vazar texto técnico do servidor.
 */
export function mensagemErroEstornoMovimento(erro: ErroEstornoMovimento): string {
  const dados = (erro.corpo && typeof erro.corpo === 'object' ? erro.corpo : {}) as Record<string, unknown>;
  const codigo = typeof dados.codigo === 'string' ? dados.codigo : null;
  if (erro.status === 409 && codigo === CODIGO_MOVIMENTO_EM_RATEIO_FECHADO) {
    const dia = typeof dados.dia === 'string' && dados.dia !== '' ? formatarData(dados.dia) : erro.movimentoCriadoEm ? formatarDataLocal(erro.movimentoCriadoEm) : null;
    return `Este movimento entrou ${dia ? `no rateio fechado de ${dia}` : 'num rateio fechado'}. Estorne o fechamento do rateio antes.`;
  }
  if (erro.status === 400 && codigo === CODIGO_MOTIVO_OBRIGATORIO) return MENSAGEM_MOTIVO_OBRIGATORIO;
  if (erro.status === 403) return MENSAGEM_SEM_PERMISSAO_ESTORNO_MOVIMENTO;
  return MENSAGEM_FALHA_ESTORNO_MOVIMENTO;
}
